import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Layout from './components/layout/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import CookieConsent from './components/common/CookieConsent';

const DEALS_REFRESH_KEY = 'amazononlines-amazon-deals-refresh';
const DEALS_REFRESH_ENDPOINT = '/api/products/deals-refresh';

const HomePage      = lazy(() => import('./pages/HomePage'));
const SearchPage    = lazy(() => import('./pages/SearchPage'));
const ProductPage   = lazy(() => import('./pages/ProductPage'));
const CategoryPage  = lazy(() => import('./pages/CategoryPage'));
const WishlistPage  = lazy(() => import('./pages/WishlistPage'));
const ProfilePage   = lazy(() => import('./pages/ProfilePage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const NotFoundPage  = lazy(() => import('./pages/NotFoundPage'));
const DisclosurePage = lazy(() => import('./pages/DisclosurePage'));
const AboutPage     = lazy(() => import('./pages/AboutPage'));
const BlogPage      = lazy(() => import('./pages/BlogPage'));
const BlogPostPage  = lazy(() => import('./pages/BlogPostPage'));
const PrivacyPage   = lazy(() => import('./pages/PrivacyPage'));
const AmazonCallbackPage = lazy(() => import('./pages/AmazonCallbackPage'));

const fallback = <LoadingSpinner fullPage />;

const PrimeAmazonDealsRefresh = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const alreadyRanForThisSession = window.sessionStorage.getItem(DEALS_REFRESH_KEY) === '1';
    if (alreadyRanForThisSession) return;

    window.sessionStorage.setItem(DEALS_REFRESH_KEY, '1');

    const refresh = async () => {
      try {
        await fetch(DEALS_REFRESH_ENDPOINT, {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
        });
      } catch {
        // Server-side access is the safe fallback; if it fails, we still keep the page responsive.
      }

      window.setTimeout(() => {
        if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/auth/amazon/callback')) {
          window.location.reload();
        }
      }, 1200);
    };

    void refresh();
  }, []);

  return null;
};

const App: React.FC = () => (
  <BrowserRouter>
    <PrimeAmazonDealsRefresh />
    <Suspense fallback={fallback}>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="product/:asin" element={<ProductPage />} />
          <Route path="categories/:nodeId" element={<CategoryPage />} />
          <Route path="wishlist" element={<WishlistPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="disclosure" element={<DisclosurePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="auth/amazon/callback" element={<AmazonCallbackPage />} />
          <Route path="api/auth/amazon/callback" element={<AmazonCallbackPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
    <CookieConsent />
  </BrowserRouter>
);

export default App;
